function normalizeCartDrawerLineIndexes(root = document) {
  const items = root.querySelectorAll('#CartDrawer .cart-item');

  items.forEach((item, index) => {
    const line = index + 1;
    item.id = `CartDrawer-Item-${line}`;

    const quantityInput = item.querySelector('.quantity__input');
    if (quantityInput) {
      quantityInput.id = `Drawer-quantity-${line}`;
      quantityInput.dataset.index = String(line);
    }

    const removeElement = item.querySelector('cart-remove-button');
    if (removeElement) {
      removeElement.id = `CartDrawer-Remove-${line}`;
      removeElement.dataset.index = String(line);
    }

    const errorElement = item.querySelector('.cart-item__error');
    if (errorElement) errorElement.id = `CartDrawer-LineItemError-${line}`;
  });
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    normalizeCartDrawerLineIndexes(this);
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    // WizzyWand: mount the drawer directly under <body> so its fixed/right
    // positioning is relative to the viewport, including inside Shopify's editor.
    if (this.parentElement === document.body) return;

    const mountedDrawer = document.body.querySelector('cart-drawer[data-wizzy-body-mounted="true"]');
    if (mountedDrawer && mountedDrawer !== this) mountedDrawer.remove();

    this.dataset.wizzyBodyMounted = 'true';
    document.body.appendChild(this);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    normalizeCartDrawerLineIndexes(this);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    setTimeout(() => {
      const container = this.querySelector('.drawer__inner');
      const focusElement = this.querySelector('.drawer__close') || container;
      if (container && focusElement) trapFocus(container, focusElement);
    }, 330);

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections?.[section.id];
      if (!html) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;

      const newContent = this.getSectionInnerHTML(html, section.selector);
      if (newContent !== null) sectionElement.innerHTML = newContent;
    });

    normalizeCartDrawerLineIndexes(this);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const target = documentFragment.querySelector(selector);
    return target ? target.innerHTML : null;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();
    normalizeCartDrawerLineIndexes(this.closest('cart-drawer') || this);
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
